// Public club / party leaderboard: https://app.puttr.co/leaderboard/:clubId
// One card per game that has scores posted under this clubId (the API builds
// the list from club_leaderboard itself, so party hosts and creator games show
// up without any club_games setup). Same black theme as the shared game page.
//   ?signage=1  TV mode: three boards per page, cycles pages, shows a join QR
//   ?days=7     only scores from the last N days
//   ?gameId=12  open straight into that game's full board
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';

import '../games/GameView.css';
import './ClubLeaderboard.css';
import { API_URL } from '../../constants/Strings';
import { ReactComponent as Logo } from '../../images/logo_header.svg';
import LeaderboardCard from './LeaderboardCard';
import PuttrSpinner from '../common/PuttrSpinner';
import OpenInApp, { isMobile } from '../common/OpenInApp';

const PER_PAGE = 3;            // boards per signage page
const CYCLE_MS = 15 * 1000;    // signage page turn
const REFRESH_MS = 30 * 1000;  // score refresh

// A logo URL that 404s should leave a clean header, not a broken-image icon
const hideBroken = (e) => { e.target.style.display = 'none'; };

export default function ClubLeaderboard() {

  const { clubId } = useParams();
  const query = useMemo(() => new URLSearchParams(window.location.search), []);
  const signage = query.get('signage') == 1;
  const days = query.get('days');

  const [leaderboards, setLeaderboards] = useState(null);
  const [club, setClub] = useState(null);
  const [failed, setFailed] = useState(false);
  const [page, setPage] = useState(0);
  const [selectedId, setSelectedId] = useState(query.get('gameId'));

  useEffect(() => {
    document.body.classList.add('dark');
    return () => document.body.classList.remove('dark');
  }, []);

  useEffect(() => {
    let url = API_URL + '/club/leaderboard/games/' + encodeURIComponent(clubId);
    if (days) url += '/' + encodeURIComponent(days);

    const load = () => {
      axios.get(url)
        .then(res => {
          setLeaderboards(Array.isArray(res.data) ? res.data : []);
          setFailed(false);
        })
        .catch(() => setFailed(true));
    };

    load();
    const timer = setInterval(load, REFRESH_MS);
    return () => clearInterval(timer);
  }, [clubId, days]);

  useEffect(() => {
    axios.get(API_URL + '/club/info/' + encodeURIComponent(clubId))
      .then(res => setClub(Array.isArray(res.data) && res.data[0] ? res.data[0] : null))
      .catch(() => setClub(null));
  }, [clubId]);

  // Boards with at least one score; the kit's featured game leads.
  const boards = useMemo(() => {
    const list = (leaderboards || []).filter(lb => lb.scores && lb.scores.length > 0);
    const featured = club && club.brandKit ? club.brandKit.featuredGameId : null;
    if (featured) list.sort((a, b) => (b.leaderboard.id == featured) - (a.leaderboard.id == featured));
    return list;
  }, [leaderboards, club]);

  const pageCount = Math.max(1, Math.ceil(boards.length / PER_PAGE));

  useEffect(() => {
    if (!signage || pageCount < 2) return;
    const timer = setInterval(() => setPage(p => (p + 1) % pageCount), CYCLE_MS);
    return () => clearInterval(timer);
  }, [signage, pageCount]);

  const kit = club && club.brandKit ? club.brandKit : null;
  const title = (kit && kit.leaderboardTitle) || 'Leaderboard';
  const subtitle = (kit && (kit.title || kit.brandName)) || (club && club.clubName) || clubId;
  const selected = selectedId ? boards.find(lb => lb.leaderboard.id == selectedId) : null;

  const renderBrand = () => {
    if (!club || !club.clubLogo) return null;
    // Venue clubs carry a header colour their logo was designed for; Brand Kit
    // logos are made for the dark party screens and sit straight on the black.
    const band = !kit && club.headerColor ? { backgroundColor: '#' + club.headerColor } : null;
    return (
      <div className={band ? 'lbBrand lbBrandBand' : 'lbBrand'} style={band}>
        <img src={club.clubLogo} alt={club.clubName || 'Logo'} className="lbBrandLogo" onError={hideBroken} />
        {kit && kit.cohostLogoUri ? <span className="lbBrandX">&times;</span> : null}
        {kit && kit.cohostLogoUri ? <img src={kit.cohostLogoUri} alt={kit.cohostName || 'Co-host'} className="lbBrandLogo" onError={hideBroken} /> : null}
      </div>
    );
  };

  const renderBoards = () => {
    if (failed && !leaderboards) {
      return <div className="gcard center lbEmpty">We couldn't load this leaderboard. Check your connection and try again.</div>;
    }
    if (!leaderboards) return <PuttrSpinner />;
    if (boards.length === 0) {
      return (
        <div className="gcard center lbEmpty">
          <div className="bold">No scores yet</div>
          <div className="muted" style={{ marginTop: 4 }}>
            {days ? 'Nobody has posted a score in the last ' + days + ' days.' : 'Finish a game to get on the board.'}
          </div>
        </div>
      );
    }

    if (selected) {
      return (
        <LeaderboardCard
          leaderboard={selected}
          fullBoard
          signage={signage}
          onBack={signage ? null : () => setSelectedId(null)}
        />
      );
    }

    const shown = signage ? boards.slice(page * PER_PAGE, page * PER_PAGE + PER_PAGE) : boards;

    return (
      <div className={signage ? 'lbGrid lbGridSignage' : 'lbGrid'}>
        {shown.map(lb => (
          <LeaderboardCard
            key={lb.leaderboard.id}
            leaderboard={lb}
            signage={signage}
            onSelect={signage ? null : () => { setSelectedId(lb.leaderboard.id); window.scrollTo(0, 0); }}
          />
        ))}
      </div>
    );
  };

  const renderSignageFooter = () => {
    const joinUrl = 'https://app.puttr.co/leaderboard/' + encodeURIComponent(clubId);
    const qrPath = 'https://api.qrserver.com/v1/create-qr-code/?size=240x240&margin=8&data=' + encodeURIComponent(joinUrl);
    return (
      <div className="lbSignageFoot">
        <img src={qrPath} width="96" height="96" alt="QR code" className="lbQr" />
        <div>
          <div className="bold">Follow the leaderboard</div>
          <div className="muted">Scan to see every board on your phone</div>
        </div>
        {pageCount > 1 ? (
          <div className="lbDots">
            {Array.from({ length: pageCount }).map((_, i) => <span key={i} className={i === page ? 'lbDot lbDotOn' : 'lbDot'} />)}
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <div className={signage ? 'darkPage lbSignage' : 'darkPage'}>

      <div className="topBar">
        <a href="https://puttr.co?utm_source=leaderboard" aria-label="Puttr"><Logo className="logo" /></a>
        {signage ? null : <OpenInApp path="" label={isMobile() ? 'Open in app' : 'Get the app'} />}
      </div>

      <div className="page">

        {renderBrand()}

        <div className="center lbHeading">
          <div className="lbPageTitle">{title}</div>
          <div className="muted">{subtitle}{days ? ' · last ' + days + ' days' : ''}</div>
        </div>

        {renderBoards()}

        {signage ? renderSignageFooter() : (
          <div className="footNote">Scores post automatically when a game finishes on a Puttr green.</div>
        )}

      </div>

    </div>
  );

}
